import { useState } from "react";
import { useUpdateGoLive } from "../../helpers/hooks/use-update-go-live";

interface Props {
  customerId: string;
  dashboardUnlockedAt: string | null;
  shouldPushPricesEnabledAt: string | null;
}

function toInputDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/**
 * Inline date editors for the two go-live fields. Each field has its own
 * "Edit" toggle so clicking one doesn't disturb the other. On save, the
 * backend logs a 'go_live_override' timeline event and re-runs the health
 * score so the onboarding signal reflects the change immediately.
 */
export function GoLiveDateEditor({
  customerId,
  dashboardUnlockedAt,
  shouldPushPricesEnabledAt,
}: Props) {
  const update = useUpdateGoLive(customerId);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      <DateField
        label="Dashboard unlocked"
        value={dashboardUnlockedAt}
        onSave={(v) =>
          update.mutate({ dashboardUnlockedAt: v })
        }
        saving={update.isPending}
      />
      <DateField
        label="Pricing go-live"
        value={shouldPushPricesEnabledAt}
        onSave={(v) =>
          update.mutate({ shouldPushPricesEnabledAt: v })
        }
        saving={update.isPending}
      />
    </div>
  );
}

function DateField({
  label,
  value,
  onSave,
  saving,
}: {
  label: string;
  value: string | null;
  onSave: (v: string | null) => void;
  saving: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(toInputDate(value));

  const currentIsoDate = toInputDate(value);

  if (!editing) {
    return (
      <div>
        <dt className="text-xs text-content-tertiary mb-0.5">{label}</dt>
        <dd className="text-sm text-content-primary flex items-center gap-2">
          <span>
            {value ? (
              new Date(value).toLocaleDateString("en-US", {
                timeZone: "America/New_York",
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            ) : (
              <span className="text-content-tertiary">—</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              setDraft(currentIsoDate);
              setEditing(true);
            }}
            className="text-xs text-primary hover:underline"
          >
            Edit
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div>
      <dt className="text-xs text-content-tertiary mb-0.5">{label}</dt>
      <dd className="flex items-center gap-2">
        <input
          type="date"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={saving}
          className="px-2 py-1 text-sm border border-border-primary rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <button
          type="button"
          onClick={() => {
            // Empty input → send null (clear the field)
            onSave(draft ? new Date(draft).toISOString() : null);
            setEditing(false);
          }}
          disabled={saving}
          className="text-xs px-2 py-1 font-medium text-white bg-primary rounded hover:opacity-90 disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          disabled={saving}
          className="text-xs text-content-tertiary hover:text-content-primary"
        >
          Cancel
        </button>
      </dd>
    </div>
  );
}
